import formatDate from "astrid-helpers/src/formatDate";

import createDocumentData from "../../utils/createDocumentData";

import getArticleChannelData from "../articles/getArticleChannelData";

import articleHasChannelDates from "./articleHasChannelDates";
import distributionStatuses from "./constants/distributionStatuses";
import { Distribution } from "./types/Distribution";

// All dates in here are calendar dates ("yyyy-MM-dd"), compared as strings, and today is the date
// in Stockholm. They only become a moment for scheduledAt, as midnight UTC, which is on that date in
// Stockholm, where queueScheduledDistributions looks for it.

const dayInMs = 24 * 60 * 60 * 1000;

function getScheduledDate(date, daysToSubtract = 0) {
	return date && new Date(Date.parse(date) - daysToSubtract * dayInMs).toISOString().slice(0, 10);
}

function getDaysBetween(from, to) {
	return (Date.parse(to) - Date.parse(from)) / dayInMs;
}

function updateDeliveryBasedOnRelease({ delivery, scheduledReleaseDate, today }) {
	if (delivery.metadata) {
		if (scheduledReleaseDate > today) {
			// this makes it possible to clear all previously scheduled artifact distributions and recreate them if the user does "only metadata delivery (advanced)" over and over with a future release date.
			return {
				...delivery,
				artifact: true,
			};
		}
	}
	return delivery;
}

function schedule(firebase, { publisher, article, channel, delivery, scheduledAt }) {
	return Distribution.parse(
		createDocumentData(firebase, "distributions", {
			publisher,
			article,
			channel,
			delivery,
			status: distributionStatuses.SCHEDULED,
			scheduledAt: firebase.firestore.Timestamp.fromDate(new Date(scheduledAt)),
		}),
	);
}

function queue(firebase, { publisher, article, channel, delivery }) {
	return Distribution.parse(
		createDocumentData(firebase, "distributions", {
			publisher,
			article,
			channel,
			delivery,
		}),
	);
}

function takeDown(
	firebase,
	{ scheduledTakedownDate, supportsTakedownDate, forceDeliveryOfMetadataAndFilesSimultaneously, data },
) {
	const { delivery, today } = data;
	if (delivery.metadata) {
		const deliveryWithoutArtifact = {
			...delivery,
			artifact: !!forceDeliveryOfMetadataAndFilesSimultaneously,
		};
		if (!supportsTakedownDate && scheduledTakedownDate > today) {
			return schedule(firebase, {
				...data,
				delivery: deliveryWithoutArtifact,
				scheduledAt: scheduledTakedownDate,
			});
		}
		return queue(firebase, { ...data, delivery: deliveryWithoutArtifact });
	}
}

function announce(
	firebase,
	{ announcementDate, supportsAnnouncementDate, forceDeliveryOfMetadataAndFilesSimultaneously, data },
) {
	const { delivery } = data;
	if (delivery.metadata && !forceDeliveryOfMetadataAndFilesSimultaneously) {
		const deliveryWithoutArtifact = { ...delivery, artifact: false };
		if (!supportsAnnouncementDate && announcementDate > data.today) {
			return schedule(firebase, { ...data, delivery: deliveryWithoutArtifact, scheduledAt: announcementDate });
		}
		return queue(firebase, { ...data, delivery: deliveryWithoutArtifact });
	}
}

function release(
	firebase,
	{
		article,
		announcementDate,
		scheduledReleaseDate,
		releaseDateGracePeriod,
		forceDeliveryOfMetadataAndFilesSimultaneously,
		data,
	},
) {
	const { today, delivery } = data;
	let releases = [];

	const artifact = delivery.artifact && article?.artifact?.ref;
	const scheduleArtifact = artifact && scheduledReleaseDate > today;
	const queueArtifact = artifact && scheduledReleaseDate <= today;

	const scheduleMetadata = delivery.metadata && scheduledReleaseDate > today;

	const metadataCanBeSentRightAway =
		(!forceDeliveryOfMetadataAndFilesSimultaneously && !announcementDate) || scheduledReleaseDate <= today;
	const queueMetadata = delivery.metadata && metadataCanBeSentRightAway;

	if (queueMetadata || queueArtifact) {
		releases.push(
			queue(firebase, {
				...data,
				delivery: {
					metadata: queueMetadata || false,
					artifact: queueArtifact || false,
				},
			}),
		);
	}

	if (scheduleMetadata || scheduleArtifact) {
		releases.push(
			schedule(firebase, {
				...data,
				delivery: {
					metadata: scheduleMetadata || false,
					artifact: scheduleArtifact || false,
				},
				scheduledAt: getScheduledDate(scheduledReleaseDate, releaseDateGracePeriod),
			}),
		);
	}

	return releases;
}

export default function queueOrScheduleDistributions(
	firebase,
	{ publisher, article, channel, delivery, today: now = new Date() },
) {
	const today = formatDate(now);

	const {
		supportsAnnouncementDate,
		supportsTakedownDate,
		releaseDateGracePeriod,
		takedownDateGracePeriod,
		forceDeliveryOfMetadataAndFilesSimultaneously,
		preventTakedown,
	} = channel;

	if (delivery.metadata && !articleHasChannelDates(article, channel.id)) {
		throw new Error("At least one of announcement date, release date or takedown date must be set");
	}

	const announcementDate = getArticleChannelData(article, channel.id, "announcementDate") || null;

	const releaseDate = getArticleChannelData(article, channel.id, "releaseDate")
		? getArticleChannelData(article, channel.id, "releaseDate")
		: articleHasChannelDates(article, channel.id)
		? null
		: today;

	const takedownDate = getArticleChannelData(article, channel.id, "takedownDate") || null;

	const gracePeriod =
		announcementDate && releaseDate
			? Math.max(0, Math.min(getDaysBetween(announcementDate, releaseDate), releaseDateGracePeriod))
			: releaseDateGracePeriod;
	const scheduledReleaseDate = getScheduledDate(releaseDate, gracePeriod);
	const scheduledTakedownDate = getScheduledDate(takedownDate, takedownDateGracePeriod);

	const data = {
		publisher,
		article,
		channel,
		delivery: updateDeliveryBasedOnRelease({ article, channel, delivery, scheduledReleaseDate, today }),
		today,
	};

	const distributions = [];

	const takedownAndIgnoreEverythingElse =
		data.delivery.metadata &&
		takedownDate &&
		((releaseDate && takedownDate <= releaseDate) ||
			(announcementDate && takedownDate <= announcementDate) ||
			takedownDate <= today) &&
		!preventTakedown;

	if (takedownAndIgnoreEverythingElse) {
		return [
			takeDown(firebase, {
				scheduledTakedownDate,
				supportsTakedownDate,
				forceDeliveryOfMetadataAndFilesSimultaneously,
				data,
			}),
		];
	}

	const releaseCoversAnnouncement =
		releaseDate && data.delivery.metadata && announcementDate && scheduledReleaseDate <= announcementDate;

	if (announcementDate && !releaseCoversAnnouncement && (!releaseDate || scheduledReleaseDate > today)) {
		distributions.push(
			announce(firebase, {
				announcementDate,
				supportsAnnouncementDate,
				forceDeliveryOfMetadataAndFilesSimultaneously,
				data,
			}),
		);
	}

	if (releaseDate) {
		distributions.push(
			release(firebase, {
				article,
				announcementDate,
				scheduledReleaseDate,
				forceDeliveryOfMetadataAndFilesSimultaneously,
				data,
			}),
		);
	}

	if (
		(!preventTakedown &&
			takedownDate &&
			!supportsTakedownDate &&
			scheduledTakedownDate > today &&
			releaseDate !== takedownDate) ||
		(takedownDate && !releaseDate && !announcementDate)
	) {
		distributions.push(
			takeDown(firebase, {
				scheduledTakedownDate,
				supportsTakedownDate,
				forceDeliveryOfMetadataAndFilesSimultaneously,
				data,
			}),
		);
	}

	return distributions.flat().filter(Boolean);
}
